import { useCallback, useMemo } from 'react'

import {
  type SilkHomeCarouselResponse,
  type SilkHomePageModule,
} from '@/api'
import { filterCarouselSlides } from '@/constants/tenant-features'
import { stripNavArrow } from '@/shared/lib/strip-nav-arrow'
import { useTenantFeatures } from '@/shared/hooks/useTenantFeatures'
import type { WorksSquareConfig } from '@/api/worksSquare'
import type { PageAdLayoutGroup } from '@/api/page-ads'

import { useHomeSiteContext } from '../context/HomeSiteProvider'

const INIT_CAROUSEL: SilkHomeCarouselResponse = {
  auto_play_ms: 6000,
  carousel_style: 'classic',
  slides: [],
  total: 0,
}

const DEFAULT_SQUARE: WorksSquareConfig = {
  title: '',
  subtitle: '',
  empty: '',
  default_order_by: 'curated',
  show_category_tabs: true,
  page_size: 24,
}

function normalizePreviewList<T>(raw?: { items?: T[] | null; total?: number } | null) {
  return {
    items: raw?.items ?? [],
    total: raw?.total ?? 0,
  }
}

export function modulePortalKey(mod: SilkHomePageModule): string {
  return mod.portal_key || `home.module.${mod.id}`
}

export function moduleField(mod: SilkHomePageModule, key: string, fallback = ''): string {
  const value = mod.fields.find((f) => f.key === key)?.value || fallback
  if (key === 'view_all') return stripNavArrow(value)
  return value
}

/** 用户端首页聚合(GET /home/site,含 activity_banner / works_square / page_ads) */
export function useHomeSite() {
  const { site, loading, error, refresh } = useHomeSiteContext()
  const { features } = useTenantFeatures()

  const carousel = useMemo(() => site?.carousel ?? INIT_CAROUSEL, [site?.carousel])
  const modules = useMemo(() => site?.page?.modules ?? [], [site?.page?.modules])

  const slides = useMemo(
    () => filterCarouselSlides(carousel.slides, features),
    [carousel.slides, features],
  )

  const isBlockEnabled = useCallback(
    (portalKey: string) => site?.blocks?.[portalKey] !== false,
    [site?.blocks],
  )

  const isModuleVisible = useCallback(
    (mod: SilkHomePageModule) => isBlockEnabled(modulePortalKey(mod)),
    [isBlockEnabled],
  )

  const contestsPreview = useMemo(
    () => normalizePreviewList(site?.contests),
    [site?.contests],
  )

  const worksPreview = useMemo(
    () => normalizePreviewList(site?.works),
    [site?.works],
  )

  const worksSquare = useMemo(
    () => ({ ...DEFAULT_SQUARE, ...site?.works_square }),
    [site?.works_square],
  )

  const pageAdGroups = useMemo(
    () => (site?.page_ads?.groups ?? []) as PageAdLayoutGroup[],
    [site?.page_ads?.groups],
  )

  return {
    site,
    carousel: { ...carousel, slides, total: slides.length },
    modules,
    contestsPreview,
    worksPreview,
    worksSquare,
    pageAdGroups,
    moduleField,
    modulePortalKey,
    isBlockEnabled,
    isModuleVisible,
    loading,
    error,
    refresh: () => refresh(),
  }
}
